/**
 * Centralized typed accessor for frontend env variables.
 *
 * Exclusive dotenv (see `vite.config.ts` + `H365_DOTENV`):
 *   npm run dev              → .env.local
 *   npm run dev:development  → .env.development
 *   npm run build / build:prod → .env.production
 *   npm run build:dev        → .env.development
 *
 * Importing this module triggers eager validation; a missing required var
 * causes a clear, named error at app boot instead of a confusing runtime
 * failure deep in the auth flow.
 *
 * Adding a new VITE_* var:
 *   1. Add an entry to `env` below.
 *   2. Document it in `.env.example`.
 *   3. Put values in `.env.local` (laptop) / `.env.development` / `.env.production`.
 *   4. Add it to Vercel (or CI) env vars when deploying — platform wins over files.
 */
function required(name: string): string {
  const value = (import.meta.env as Record<string, string | undefined>)[name];
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(
      `Missing required env variable: ${name}. Check .env.local / .env.${import.meta.env.MODE} ` +
        `locally, or the host's environment variables in deploys. After adding a new VITE_* key, restart \`npm run dev\`.`,
    );
  }
  return value;
}

/**
 * An optional flag. Never throws — a missing optional var is not an error, and
 * this module is validated eagerly on import. Anything that isn't exactly
 * "true" reads as `fallback`, so a typo in a deploy fails closed rather than
 * half-enabling a feature.
 */
function optional(name: string): string | undefined {
  const source = (import.meta.env ?? {}) as Record<string, string | undefined>;
  const value = source[name];
  if (value == null || value.trim() === "") return undefined;
  return value.trim();
}

function optionalBool(name: string, fallback: boolean): boolean {
  const source = (import.meta.env ?? {}) as Record<string, string | undefined>;
  const value = source[name];
  if (value == null || value.trim() === "") return fallback;
  return value.trim().toLowerCase() === "true";
}

export const env = {
  /** Supabase project URL for this environment. */
  SUPABASE_URL: required("VITE_SUPABASE_URL"),
  /** Supabase publishable (anon) key for this environment. Browser-safe. */
  SUPABASE_PUBLISHABLE_KEY: required("VITE_SUPABASE_PUBLISHABLE_KEY"),
  /** 20-char project ref. Used for the `sb-<ref>-auth-token` localStorage key. */
  SUPABASE_PROJECT_ID: required("VITE_SUPABASE_PROJECT_ID"),
  /**
   * Base URL of the Express backend for this env.
   *   laptop         → http://localhost:3000
   *   development    → https://api-dev.h365greekportal.com
   *   production     → https://api.h365greekportal.com
   */
  API_URL: required("VITE_API_SERVER_URL"),
  /**
   * Show the "Other" (manual) licensor in the licensing UI — its table chip, its
   * drawer tab, and the licensor filter. Unset or anything other than "true"
   * hides it. A record that already has manual data recorded shows it either
   * way; see `modules/Licensing/lib/visibleGroups`.
   */
  SHOW_OTHER_LICENSOR: optionalBool("VITE_LICENSING_SHOW_OTHER_LICENSOR", false),
  /**
   * The Handed Off lane: a receipt showing fully-assigned orders for three
   * days. Behind a flag because whether it earns its place is an open question
   * with the production team (guide §0.8 #1) — removing it is one flag flip.
   */
  PRODUCTION_HANDED_OFF_LANE: optionalBool("VITE_PRODUCTION_HANDED_OFF_LANE", true),
  /**
   * First-party client → api-server (`x-client-api-key`). Must match CLIENT_API_SECRET.
   * Optional while portal routes are JWT-only; required for `authenticateClientApp` APIs.
   */
  CLIENT_API_KEY: optional("VITE_CLIENT_API_KEY"),
  /**
   * Group store site origin for Open Store / Copy Link on order detail.
   *   .env.local         → http://localhost:8084
   *   .env.development   → https://dev-group-store.habits365greek.com
   *   .env.production    → https://group-store.habits365greek.com
   */
  H365_STOREFRONT_URL: required("VITE_H365_STOREFRONT_URL"),
} as const;

export type Env = typeof env;
